import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Download,
  History,
  RefreshCw,
  ArrowLeft,
  Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MedicielImportPage = ({ onBack }) => {
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLogs, setImportLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImportLogs();
  }, []);

  const fetchImportLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/pharmacy/import/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Format de fichier non supporté. Utilisez CSV ou Excel (.xlsx, .xls)');
      return;
    }

    setUploading(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(`${API_URL}/pharmacy/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      toast.success(`Import terminé : ${response.data.created} créés, ${response.data.updated} mis à jour`);
      fetchImportLogs();
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'import';
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5 text-[#8B7355]" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#5D4E37]">Import Mediciel</h1>
            <p className="text-[#8B7355]">Importez vos fichiers de stock depuis Mediciel / Logipharm</p>
          </div>
        </div>

        {/* Instructions */}
        <Card className="mb-6 border-[#E8DDD4] bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Comment exporter depuis Mediciel :</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Ouvrez Mediciel sur votre ordinateur</li>
                  <li>Allez dans <strong>Stock &gt; Liste des produits</strong></li>
                  <li>Cliquez sur <strong>Exporter</strong> (icône Excel ou CSV)</li>
                  <li>Enregistrez le fichier sur votre ordinateur</li>
                  <li>Importez-le ici</li>
                </ol>
                <p className="mt-2 text-xs">
                  <strong>Colonnes reconnues :</strong> Code, Nom, Prix, Stock, Catégorie, Marque
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Zone */}
        <Card className="mb-6 border-2 border-dashed border-[#E8DDD4] hover:border-[#C45C26] transition-colors">
          <CardContent className="p-8">
            <div className="text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              
              {uploading ? (
                <div className="py-8">
                  <Loader2 className="w-16 h-16 mx-auto text-[#C45C26] animate-spin mb-4" />
                  <p className="text-[#5D4E37] font-medium">Import en cours...</p>
                  <p className="text-[#8B7355] text-sm">Cela peut prendre quelques instants</p>
                </div>
              ) : (
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="w-20 h-20 bg-[#C45C26]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-10 h-10 text-[#C45C26]" />
                  </div>
                  <p className="text-[#5D4E37] font-medium text-lg mb-2">
                    Cliquez pour sélectionner un fichier
                  </p>
                  <p className="text-[#8B7355] text-sm mb-4">
                    ou glissez-déposez votre fichier ici
                  </p>
                  <div className="flex justify-center gap-2">
                    <Badge className="bg-green-100 text-green-700 border-0">
                      <FileSpreadsheet className="w-3 h-3 mr-1" />
                      CSV
                    </Badge>
                    <Badge className="bg-green-100 text-green-700 border-0">
                      <FileSpreadsheet className="w-3 h-3 mr-1" />
                      Excel (.xlsx)
                    </Badge>
                  </div>
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Import Result */}
        {importResult && (
          <Card className={`mb-6 border-0 shadow-lg ${importResult.error ? 'bg-red-50' : 'bg-green-50'}`}>
            <CardContent className="p-6">
              {importResult.error ? (
                <div className="flex items-start gap-3">
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800">Erreur d'import</p>
                    <p className="text-red-700">{importResult.error}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <p className="font-semibold text-green-800">Import réussi !</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{importResult.created}</p>
                      <p className="text-sm text-green-700">Produits créés</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">{importResult.updated}</p>
                      <p className="text-sm text-blue-700">Produits mis à jour</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{importResult.skipped}</p>
                      <p className="text-sm text-amber-700">Lignes ignorées</p>
                    </div>
                  </div>

                  {importResult.columns_detected && Object.keys(importResult.columns_detected).length > 0 && (
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium text-green-800 mb-2">Colonnes détectées :</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(importResult.columns_detected).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key}: {value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="bg-amber-100 rounded-lg p-4">
                      <p className="text-sm font-medium text-amber-800 mb-2">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        Erreurs ({importResult.errors.length}) :
                      </p>
                      <ul className="text-xs text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                        {importResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                        {importResult.errors.length > 5 && (
                          <li className="italic">... et {importResult.errors.length - 5} autres erreurs</li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Import History */}
        <Card className="border-[#E8DDD4]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#5D4E37] flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historique des imports
                </CardTitle>
                <CardDescription>Derniers fichiers importés</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={fetchImportLogs}>
                <RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#C45C26] mx-auto" />
              </div>
            ) : importLogs.length === 0 ? (
              <div className="text-center py-8 text-[#8B7355]">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun import effectué</p>
              </div>
            ) : (
              <div className="space-y-3">
                {importLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className="flex items-center justify-between p-4 bg-[#FDF8F3] rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-8 h-8 text-[#C45C26]" />
                      <div>
                        <p className="font-medium text-[#5D4E37]">{log.filename}</p>
                        <p className="text-xs text-[#8B7355]">{formatDate(log.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-green-600">+{log.created}</span>
                      <span className="text-blue-600">↻{log.updated}</span>
                      {log.skipped > 0 && <span className="text-amber-600">⚠{log.skipped}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MedicielImportPage;
